import { LOGOUT, RECEIVE_INITIAL_STATE } from "@/components/access/ducks/Actions"
import {
  ADD_EMAIL_DRAFT_SUCCESS,
  ADD_TEST_RECIPIENT,
  CHANGE_REMINDER_BODY,
  CHANGE_REMINDER_SUBJECT,
  CLEAR_TEST_RECIPIENT,
  DELETE_EMAIL_DRAFT_SUCCESS,
  FETCH_EMAIL_DRAFTS,
  GET_SELECTED_RECIPIENTS,
  RECEIVE_EMAIL_DRAFTS,
  RECEIVE_OUTBOUND_MESSAGES,
  RECEIVE_SELECTED_RECIPIENTS,
  TOGGLE_DISABLE_REMINDER,
  TOGGLE_INCLUDE_INVITED_MEMBERS,
  TOGGLE_ONBOARD_AS_ADMIN,
  TURN_OFF_LOADING_RECIPIENTS,
  TURN_OFF_TEST_EMAIL_SENDING,
  TURN_OFF_TEST_EMAIL_SENT,
  TURN_ON_TEST_EMAIL_SENDING,
  TURN_ON_TEST_EMAIL_SENT,
  UPDATE_ADMIN_EMAIL_TYPE,
  UPDATE_DRAFT_ID,
  UPDATE_EMAIL_DRAFT_SUCCESS,
  UPDATE_FROM_NAME,
  UPDATE_RECIPIENTS,
  UPDATE_REMINDER_EMAIL_DELAY_IN_DAYS,
} from "@/components/email/admin/ducks/Actions"
import { EMAIL_SENT_RESET, UPDATE_GROUP_EMAIL_TYPE } from "@/components/email/ducks/Actions"
import {
  mailMergeTypes,
  parseRecipients,
  sortEmailDrafts,
  validTypes,
} from "@/components/email/helpers/EmailFunctions"
import getIsAdminFromResponse from "@/components/helpers/getIsAdminFromResponse"

/** @typedef {import('@/types').Recipient} Recipient */
/** @typedef {import('@/types').EmailDraft} EmailDraft */
export const canScheduleAdminEmail = (adminEmailType, hasLargeNumRecp) =>
  adminEmailType === "mailMerge" ||
  adminEmailType === "onboarding" ||
  ((adminEmailType === "general" || adminEmailType === "jobs") && hasLargeNumRecp)


/**
 * @typedef {Object} AdminEmailState
 * @property {boolean} loadingRecipients - Whether recipients are currently being loaded
 * @property {string} adminEmailType - The type of admin email being sent
 * @property {string} fromName - The name to send emails from
 * @property {boolean} includeInvitedMembers - Whether to include invited members in email sends
 * @property {boolean} onboardAsAdmin - Whether to onboard users as admins
 * @property {string|null} testRecipient - The test recipient email address
 * @property {boolean} testEmailSent - Whether a test email has been sent
 * @property {boolean} testEmailSending - Whether a test email is currently being sent
 * @property {number} reminderEmailDelayInDays - Delay in days for reminder emails
 * @property {string} reminderEmailSubject - Subject line for reminder emails
 * @property {string} reminderEmailBody - Body content for reminder emails
 * @property {boolean} disableReminder - Whether reminder emails are disabled
 * @property {Array} outboundMessages - List of outbound messages
 * @property {number|null} outboundMessagesNextPage - Next page token for outbound messages pagination
 * @property {number} outboundMessagesPage - Current page number for outbound messages
 * @property {number|null} outboundMessagesPageSize - Page size for outbound messages
 * @property {number} outboundMessagesTotalMessages - Total count of outbound messages
 * @property {Recipient[]} validRecipients - List of valid email recipients
 * @property {Recipient[]} invalidRecipients - List of invalid email recipients
 * @property {Recipient[]} recipients - Complete list of email recipients
 * @property {EmailDraft[]} emailDrafts - List of email drafts
 * @property {boolean} loadingEmailDrafts - Whether email drafts are currently being loaded
 * @property {boolean} hasLargeNumRecp - Whether there is a large number of recipients
 * @property {number|null} draftId - Current draft ID being edited
 */

/** @type {AdminEmailState} */
const defaultState = {
  loadingRecipients: false,
  adminEmailType: "",
  fromName: "",
  includeInvitedMembers: true,
  onboardAsAdmin: false,
  testRecipient: null,
  testEmailSent: false,
  testEmailSending: false,
  reminderEmailDelayInDays: 3,
  reminderEmailSubject: "",
  reminderEmailBody: "",
  disableReminder: false,
  outboundMessages: [],
  outboundMessagesNextPage: null,
  outboundMessagesPage: 0,
  outboundMessagesPageSize: null,
  outboundMessagesTotalMessages: 0,
  validRecipients: [],
  invalidRecipients: [],
  recipients: [],
  emailDrafts: [],
  loadingEmailDrafts: false,
  hasLargeNumRecp: false,
  draftId: null,
}

/**
 * Reducer for admin email state management
 * @param {AdminEmailState} oldState - The previous state
 * @param {Object} action - The Redux action object
 * @param {string} action.type - The action type
 * @returns {AdminEmailState} The new state
 */
const adminEmailReducer = (oldState = defaultState, action) => {
  Object.freeze(oldState)
  switch (action.type) {
    case RECEIVE_INITIAL_STATE: {
      const isAdmin = getIsAdminFromResponse(action)
      if (isAdmin) {
        return defaultState
      }
      return {}
    }

    case LOGOUT:
      return {}

    case RECEIVE_OUTBOUND_MESSAGES:
      return {
        ...oldState,
        outboundMessages: action.resp.outboundMessages,
        outboundMessagesPage: action.resp.page,
        outboundMessagesNextPage: action.resp.nextPage,
        outboundMessagesPageSize: action.resp.pageSize,
        outboundMessagesTotalMessages: action.resp.totalMessages,
      }

    case RECEIVE_SELECTED_RECIPIENTS: {
      const recipients = parseRecipients(action.recipients)
      return {
        ...oldState,
        validRecipients: recipients[0],
        invalidRecipients: recipients[1],
        recipients: action.recipients,
        hasLargeNumRecp: action.hasLargeNumRecp || false,
        loadingRecipients: false,
      }
    }

    case TURN_OFF_LOADING_RECIPIENTS:
      return {
        ...oldState,
        loadingRecipients: false,
      }

    case GET_SELECTED_RECIPIENTS:
      return {
        ...oldState,
        loadingRecipients: true,
        validRecipients: [],
        invalidRecipients: [],
        recipients: [],
        adminEmailType: "",
      }

    case UPDATE_RECIPIENTS:
      return {
        ...oldState,
        validRecipients: action.validRecipients,
        recipients: [...oldState.invalidRecipients, ...action.validRecipients],
      }

    case UPDATE_ADMIN_EMAIL_TYPE:
      if (validTypes.includes(action.adminEmailType)) {
        return {
          ...oldState,
          adminEmailType: action.adminEmailType,
        }
      }
      if (mailMergeTypes.includes(action.adminEmailType)) {
        return {
          ...oldState,
          adminEmailType: "mailMerge",
        }
      }
      return {
        ...oldState,
      }

    case UPDATE_FROM_NAME:
      return {
        ...oldState,
        fromName: action.fromName,
      }

    case TOGGLE_ONBOARD_AS_ADMIN:
      return {
        ...oldState,
        onboardAsAdmin: !oldState.onboardAsAdmin,
      }

    case TOGGLE_INCLUDE_INVITED_MEMBERS:
      return {
        ...oldState,
        includeInvitedMembers: !oldState.includeInvitedMembers,
      }

    case TOGGLE_DISABLE_REMINDER:
      return {
        ...oldState,
        disableReminder: !oldState.disableReminder,
      }

    case ADD_TEST_RECIPIENT:
      return {
        ...oldState,
        testRecipient: action.testRecipient,
      }

    case CLEAR_TEST_RECIPIENT:
      return {
        ...oldState,
        testRecipient: null,
      }

    case TURN_ON_TEST_EMAIL_SENT:
      return {
        ...oldState,
        testEmailSent: true,
      }

    case TURN_OFF_TEST_EMAIL_SENT:
      return {
        ...oldState,
        testEmailSent: false,
      }

    case TURN_ON_TEST_EMAIL_SENDING:
      return {
        ...oldState,
        testEmailSending: true,
      }

    case TURN_OFF_TEST_EMAIL_SENDING:
      return {
        ...oldState,
        testEmailSending: false,
      }

    case UPDATE_REMINDER_EMAIL_DELAY_IN_DAYS:
      return {
        ...oldState,
        reminderEmailDelayInDays: action.reminderEmailDelayInDays,
      }

    case CHANGE_REMINDER_SUBJECT:
      return {
        ...oldState,
        reminderEmailSubject: action.reminderEmailSubject,
      }

    case CHANGE_REMINDER_BODY:
      return {
        ...oldState,
        reminderEmailBody: action.reminderEmailBody,
      }

    case EMAIL_SENT_RESET:
      return defaultState

    case UPDATE_GROUP_EMAIL_TYPE:
      return {
        ...defaultState,
        emailDrafts: [...oldState.emailDrafts],
        draftId: oldState.draftId,
      }

    case FETCH_EMAIL_DRAFTS:
      return {
        ...oldState,
        loadingEmailDrafts: true,
      }

    case RECEIVE_EMAIL_DRAFTS:
      const fetched_drafts = action.emailDraftsResponse?.emailDrafts || []
      sortEmailDrafts(fetched_drafts)

      return {
        ...oldState,
        emailDrafts: fetched_drafts,
        loadingEmailDrafts: false,
      }

    case ADD_EMAIL_DRAFT_SUCCESS:
      const newDraft = action.draftAddedResponse?.draftAdded
      let newEmailDrafts = oldState.emailDrafts
      if (newDraft) {
        newEmailDrafts = [newDraft, ...oldState.emailDrafts]
        sortEmailDrafts(newEmailDrafts)
      }

      return {
        ...oldState,
        emailDrafts: newEmailDrafts,
      }

    case DELETE_EMAIL_DRAFT_SUCCESS:
      const emailDraftsAfterDelete = oldState.emailDrafts.filter(
        (d) => d.draftId !== action.draftId,
      )
      sortEmailDrafts(emailDraftsAfterDelete)

      return {
        ...oldState,
        emailDrafts: emailDraftsAfterDelete,
      }

    case UPDATE_DRAFT_ID:
      return {
        ...oldState,
        draftId: action.draftId,
      }

    case UPDATE_EMAIL_DRAFT_SUCCESS:
      const emailDraftsAfterUpdate = [...oldState.emailDrafts]
      const updatedDraft = Object.assign({}, action.draftUpdatedResponse.draftUpdated)
      const updatedDraftIdx = emailDraftsAfterUpdate.findIndex(
        (x) => x.draftId === updatedDraft.draftId,
      )
      if (updatedDraftIdx !== -1) {
        emailDraftsAfterUpdate[updatedDraftIdx] = updatedDraft
        sortEmailDrafts(emailDraftsAfterUpdate)
      }

      return {
        ...oldState,
        emailDrafts: emailDraftsAfterUpdate,
      }


    default:
      return oldState
  }
}

export default adminEmailReducer
